import { NumberGrid } from '@Animation/mechanics/base/number-grid'
import { NumberSelector } from '@Animation/mechanics/base/number-selector'
import { SPACE_INVADERS_ATLAS_FRAMES, SPACE_INVADERS_TEXTURE_KEYS } from '.'
import { tweenPromise } from '@Animation/utils'

export class SpaceInvadersNumberSelector extends NumberSelector {
  spaceship: Phaser.GameObjects.Image
  missile: Phaser.GameObjects.Image

  shipVelocity: number = 0.3
  missileVelocity: number = 0.5

  shipMovementActive: boolean = false
  missileActive: boolean = false

  missNumberIndex = -1

  constructor(
    scene: Phaser.Scene,
    numberGrid: NumberGrid,
    miniGameSelections: number[],
    preNumberSelected: () => Promise<void>,
    onNumberSelected: (number: number) => void,
  ) {
    super(scene, numberGrid, miniGameSelections, preNumberSelected, onNumberSelected)

    this.spaceship = scene.add.image(
      250,
      40,
      SPACE_INVADERS_TEXTURE_KEYS.ATLAS,
      SPACE_INVADERS_ATLAS_FRAMES.SHIP,
    )
    this.spaceship.setOrigin(0.5, 0.5)

    this.missile = scene.add.image(
      0,
      0,
      SPACE_INVADERS_TEXTURE_KEYS.ATLAS,
      SPACE_INVADERS_ATLAS_FRAMES.MISSILE,
    )

    this.missile.setOrigin(0.5, 1)
    this.missile.setVisible(false)
    this.missile.setScale(1)

    this.numberGrid.y = this.numberGrid.y + 5

    this.add(this.missile)
    this.add(this.spaceship)
  }

  async moveShipToLocation(targetX: number, fireAtLocation: boolean = false) {
    const travelTime = Math.abs(this.spaceship.x - targetX) / this.shipVelocity
    const fireTime = 150
    const progressPercent = 1 - fireTime / travelTime

    const fireMissile = tweenPromise(this.scene, {
      targets: this.missile,
      x: targetX,
      y: this.spaceship.y + 40,
      scale: 0.4,
      alpha: 1,
      duration: 150,
    })
    return tweenPromise(this.scene, {
      targets: this.spaceship,
      x: targetX,
      duration: travelTime,
      onUpdate: (tween: Phaser.Tweens.Tween) => {
        if (fireAtLocation && !this.missileActive && tween.progress >= progressPercent) {
          this.missileActive = true
          this.missile.setPosition(this.spaceship.x, this.spaceship.y + 15)
          this.missile.setScale(0.01)

          this.missile.setVisible(true)
          this.missile.setAlpha(0)
          fireMissile()
        }
      },
    })()
  }

  async targetSingleNumber(number: number): Promise<void> {
    const randomTarget = this.getRandomPosition()
    let targetX = randomTarget.x
    let targetY = 800

    if (number !== this.missNumberIndex) {
      targetX = this.numberGrid.x + this.numberGrid.numbers[number - 1].x
      targetY = this.numberGrid.y + this.numberGrid.numbers[number - 1].y
    }

    const currYPosition = this.spaceship.y

    this.shipMovementStop()
    await this.moveShipToLocation(targetX, true)
    this.shipMovementStart()

    await tweenPromise(this.scene, {
      targets: this.missile,
      y: targetY,
      duration: (targetY - currYPosition) / this.missileVelocity,
    })()

    const fadeAway = tweenPromise(this.scene, {
      targets: this.missile,
      scale: 0.1,
      alpha: 0,
      duration: 200,
    })

    this.missileActive = false

    if (number === this.missNumberIndex) {
      await fadeAway()
      return
    }

    fadeAway()
    await this.numberGrid.selectNumber(number)
  }

  shipMovementStop() {
    this.shipMovementActive = false
    this.scene.tweens.getTweensOf(this.spaceship).forEach((tween) => tween.stop())
  }

  async shipMovementStart() {
    this.shipMovementActive = true
    while (this.shipMovementActive) {
      await this.moveShipToLocation(this.getRandomPosition().x)
    }
  }

  async skip(numbers: number[]) {
    this.shipMovementStop()
    this.missile.setVisible(false)
    super.skip(numbers)
  }

  async targetNumbers(numbers: number[]) {
    let numberOfMisses = 3
    const newTargetList = [...numbers]

    while (numberOfMisses > 0) {
      const randomIndex = Math.floor(Math.random() * (numbers.length + 1))
      newTargetList.splice(randomIndex, 0, -1)
      numberOfMisses--
    }

    this.shipMovementStart()

    let trueIndex = 0
    for (let i = 0; i < newTargetList.length; i++) {
      if (this.skipToEnd) {
        return
      } else {
        if (i === 0 || (i > 0 && newTargetList[i - 1] !== -1)) {
          await this.preNumberSelected(i)
        }

        await this.targetSingleNumber(newTargetList[i])
      }

      this.onNumberSelected(trueIndex)
      if (newTargetList[i] !== this.missNumberIndex) {
        trueIndex++
      }
    }

    this.shipMovementStop()
  }
}
